// components/Laboratory/AnalyzerManagement.jsx
// Settings sub-view: register, configure, and monitor lab analyzer devices
// Supports TCP + Serial connections, ASTM/HL7 protocols, test code mapping
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Button, IconButton, Tooltip, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, Alert, Divider, Tabs, Tab,
  Stack, LinearProgress, Collapse
} from '@mui/material';
import {
  Add, Edit, Delete, Refresh, Cable, SignalWifiStatusbar4Bar,
  SignalWifiOff, PowerSettingsNew, Visibility, ExpandMore, ExpandLess,
  Science, Settings, Close, CheckCircle, Error as ErrorIcon,
  Wifi, UsbRounded, Memory, PlayArrow, BiotechOutlined
} from '@mui/icons-material';
import { analyzerService, labDepartmentService, testCatalogService } from '../../services/lis';

// ─── STATUS CHIP ──────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const config = {
    online:  { color: 'success', icon: <SignalWifiStatusbar4Bar sx={{ fontSize: 14 }} />, label: 'Online' },
    offline: { color: 'default', icon: <SignalWifiOff sx={{ fontSize: 14 }} />, label: 'Offline' },
    error:   { color: 'error',   icon: <ErrorIcon sx={{ fontSize: 14 }} />, label: 'Error' },
    unknown: { color: 'warning', icon: <Memory sx={{ fontSize: 14 }} />, label: 'Unknown' }
  };
  const c = config[status] || config.unknown;
  return <Chip size="small" icon={c.icon} label={c.label} color={c.color} variant="outlined" />;
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function AnalyzerManagement({ isDark }) {
  const [analyzers, setAnalyzers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [messagesDialog, setMessagesDialog] = useState({ open: false, analyzerId: null, analyzerName: '' });
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesPagination, setMessagesPagination] = useState({ page: 0, total: 0 });
  const [expandedRow, setExpandedRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [simulatingId, setSimulatingId] = useState(null);
  const [simResult, setSimResult] = useState(null);
  const [simDialogOpen, setSimDialogOpen] = useState(false);
  const [registeringVirtual, setRegisteringVirtual] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '', analyzerId: '', manufacturer: '', model: '', serialNumber: '',
    department: '', connectionType: 'tcp', protocol: 'astm', isActive: true,
    tcpHost: '', tcpPort: 9100, tcpMode: 'server',
    serialPort: '', baudRate: 9600, dataBits: 8, parity: 'none', stopBits: 1,
    sampleIdSource: 'order_sample_id'
  });

  // Colors
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const headerBg = isDark ? '#252525' : '#F8FAFC';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textSecondary = isDark ? '#CBD5E1' : '#475569';
  const accentBlue = '#3B82F6';

  const DEFAULT_DEPARTMENTS = [
    { _id: 'hematology', name: 'Hematology' },
    { _id: 'clinical_chemistry', name: 'Clinical Chemistry' },
    { _id: 'microbiology', name: 'Microbiology & Parasitology' },
    { _id: 'immunology', name: 'Immunology & Serology' },
    { _id: 'histopathology', name: 'Histopathology & Cytology' },
    { _id: 'molecular', name: 'Molecular Diagnostics' }
  ];

  // ─── FETCH ────────────────────────────────────────────────────────────────
  const fetchAnalyzers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await analyzerService.list();
      setAnalyzers(data.data || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load analyzers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const { data } = await labDepartmentService.list();
      const list = data.data || [];
      setDepartments(list.length > 0 ? list : DEFAULT_DEPARTMENTS);
    } catch {
      setDepartments(DEFAULT_DEPARTMENTS);
    }
  }, []);

  useEffect(() => { fetchAnalyzers(); fetchDepartments(); }, [fetchAnalyzers, fetchDepartments]);

  // ─── FORM HANDLERS ────────────────────────────────────────────────────────
  const resetForm = () => {
    setForm({
      name: '', analyzerId: '', manufacturer: '', model: '', serialNumber: '',
      department: '', connectionType: 'tcp', protocol: 'astm', isActive: true,
      tcpHost: '', tcpPort: 9100, tcpMode: 'server',
      serialPort: '', baudRate: 9600, dataBits: 8, parity: 'none', stopBits: 1,
      sampleIdSource: 'order_sample_id'
    });
    setEditingId(null);
  };

  const openCreate = () => { resetForm(); fetchDepartments(); setDialogOpen(true); };

  const openEdit = (analyzer) => {
    fetchDepartments();
    setForm({
      name: analyzer.name || '',
      analyzerId: analyzer.analyzerId || '',
      manufacturer: analyzer.manufacturer || '',
      model: analyzer.model || '',
      serialNumber: analyzer.serialNumber || '',
      department: analyzer.department?._id || analyzer.department || '',
      connectionType: analyzer.connectionType || 'tcp',
      protocol: analyzer.protocol || 'astm',
      isActive: analyzer.isActive !== false,
      tcpHost: analyzer.tcp?.host || '',
      tcpPort: analyzer.tcp?.port || 9100,
      tcpMode: analyzer.tcp?.mode || 'server',
      serialPort: analyzer.serial?.port || '',
      baudRate: analyzer.serial?.baudRate || 9600,
      dataBits: analyzer.serial?.dataBits || 8,
      parity: analyzer.serial?.parity || 'none',
      stopBits: analyzer.serial?.stopBits || 1,
      sampleIdSource: analyzer.sampleIdSource || 'order_sample_id'
    });
    setEditingId(analyzer._id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        analyzerId: form.analyzerId,
        manufacturer: form.manufacturer,
        model: form.model,
        serialNumber: form.serialNumber,
        department: form.department || undefined,
        connectionType: form.connectionType,
        protocol: form.protocol,
        isActive: form.isActive,
        sampleIdSource: form.sampleIdSource,
        tcp: form.connectionType === 'tcp' ? {
          host: form.tcpHost,
          port: parseInt(form.tcpPort) || 9100,
          mode: form.tcpMode
        } : undefined,
        serial: form.connectionType === 'serial' ? {
          port: form.serialPort,
          baudRate: parseInt(form.baudRate) || 9600,
          dataBits: parseInt(form.dataBits) || 8,
          parity: form.parity,
          stopBits: parseInt(form.stopBits) || 1
        } : undefined
      };

      if (editingId) {
        await analyzerService.update(editingId, payload);
      } else {
        await analyzerService.create(payload);
      }
      setDialogOpen(false);
      resetForm();
      fetchAnalyzers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save analyzer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await analyzerService.delete(id);
      setDeleteConfirmId(null);
      fetchAnalyzers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete analyzer');
    }
  };

  // ─── TEST CONNECTION ──────────────────────────────────────────────────────
  const handleTestConnection = async (id) => {
    setTestingId(id);
    setTestResult(null);
    try {
      const { data } = await analyzerService.testConnection(id);
      setTestResult({ id, ...data.data });
    } catch (err) {
      setTestResult({ id, connected: false, error: err.response?.data?.message || 'Test failed' });
    } finally {
      setTestingId(null);
    }
  };

  // ─── SIMULATION ────────────────────────────────────────────────────────────
  const handleRunSimulation = async (analyzerId) => {
    setSimulatingId(analyzerId);
    setSimResult(null);
    try {
      const { data } = await analyzerService.simulate(analyzerId);
      setSimResult(data);
      setSimDialogOpen(true);
      fetchAnalyzers();
    } catch (err) {
      setSimResult({
        success: false,
        message: err.response?.data?.message || 'Simulation failed',
        data: { processed: 0, skipped: 0, errors: [{ error: err.message }], details: [] }
      });
      setSimDialogOpen(true);
    } finally {
      setSimulatingId(null);
    }
  };

  const handleRegisterVirtual = async () => {
    setRegisteringVirtual(true);
    try {
      await analyzerService.registerVirtual();
      fetchAnalyzers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to register virtual analyzer');
    } finally {
      setRegisteringVirtual(false);
    }
  };

  // ─── MESSAGE LOG ──────────────────────────────────────────────────────────
  const openMessages = async (analyzer) => {
    setMessagesDialog({ open: true, analyzerId: analyzer._id, analyzerName: analyzer.name });
    setMessagesLoading(true);
    try {
      const { data } = await analyzerService.getMessages(analyzer._id, { page: 1, limit: 20 });
      setMessages(data.data || []);
      setMessagesPagination({ page: 0, total: data.pagination?.total || 0 });
    } catch { setMessages([]); }
    finally { setMessagesLoading(false); }
  };

  const fetchMessagesPage = async (page) => {
    setMessagesLoading(true);
    try {
      const { data } = await analyzerService.getMessages(messagesDialog.analyzerId, { page: page + 1, limit: 20 });
      setMessages(data.data || []);
      setMessagesPagination(prev => ({ ...prev, page }));
    } catch { /* non-fatal */ }
    finally { setMessagesLoading(false); }
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: textPrimary }}>
            Analyzer Devices
          </Typography>
          <Typography variant="body2" sx={{ color: textSecondary, mt: 0.5 }}>
            Register and manage lab analyzer connections (ASTM / HL7)
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={fetchAnalyzers}
            sx={{ borderColor: borderColor, color: textSecondary, textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={registeringVirtual ? <CircularProgress size={16} /> : <BiotechOutlined />}
            onClick={handleRegisterVirtual}
            disabled={registeringVirtual}
            sx={{
              borderColor: '#10B981',
              color: '#10B981',
              textTransform: 'none',
              '&:hover': { borderColor: '#059669', bgcolor: isDark ? 'rgba(16,185,129,0.08)' : '#ECFDF5' }
            }}
          >
            {registeringVirtual ? 'Registering...' : 'Add Virtual Simulator'}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={openCreate}
            sx={{
              bgcolor: accentBlue,
              textTransform: 'none',
              '&:hover': { bgcolor: '#2563EB' }
            }}
          >
            Add Analyzer
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Analyzer Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : analyzers.length === 0 ? (
        <Paper sx={{
          p: 6, textAlign: 'center', bgcolor: cardBg,
          border: `1px dashed ${borderColor}`, borderRadius: 2
        }}>
          <Science sx={{ fontSize: 48, color: textSecondary, mb: 2 }} />
          <Typography variant="h6" sx={{ color: textPrimary, mb: 1 }}>
            No Analyzers Registered
          </Typography>
          <Typography variant="body2" sx={{ color: textSecondary, mb: 3 }}>
            Register your lab analyzers (Mindray, Sysmex, Roche, etc.) to enable auto-population of results.
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={registeringVirtual ? <CircularProgress size={16} /> : <BiotechOutlined />}
              onClick={handleRegisterVirtual}
              disabled={registeringVirtual}
              sx={{
                borderColor: '#10B981', color: '#10B981', textTransform: 'none',
                '&:hover': { borderColor: '#059669', bgcolor: isDark ? 'rgba(16,185,129,0.08)' : '#ECFDF5' }
              }}
            >
              {registeringVirtual ? 'Registering...' : 'Add Virtual Simulator'}
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}
              sx={{ bgcolor: accentBlue, textTransform: 'none' }}>
              Add Analyzer
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {analyzers.map((analyzer) => (
            <Grid item xs={12} md={6} lg={4} key={analyzer._id}>
              <Paper sx={{
                p: 0, bgcolor: cardBg, border: `1px solid ${borderColor}`,
                borderRadius: 2, overflow: 'hidden',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.08)' }
              }}>
                {/* Card Header */}
                <Box sx={{
                  p: 2, pb: 1.5, display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', borderBottom: `1px solid ${borderColor}`,
                  bgcolor: headerBg
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textPrimary, lineHeight: 1.3 }} noWrap>
                      {analyzer.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: textSecondary, fontFamily: 'monospace' }}>
                      {analyzer.analyzerId}
                    </Typography>
                  </Box>
                  <StatusChip status={analyzer.connectionStatus} />
                </Box>

                {/* Card Body */}
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: textSecondary, fontSize: '0.7rem' }}>CONNECTION</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        {analyzer.connectionType === 'tcp'
                          ? <Wifi sx={{ fontSize: 14, color: accentBlue }} />
                          : <UsbRounded sx={{ fontSize: 14, color: '#10B981' }} />
                        }
                        <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontSize: '0.8rem' }}>
                          {analyzer.connectionType === 'tcp'
                            ? `TCP ${analyzer.tcp?.host || '*'}:${analyzer.tcp?.port || 9100}`
                            : `Serial ${analyzer.serial?.port || '—'}`
                          }
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: textSecondary, fontSize: '0.7rem' }}>PROTOCOL</Typography>
                      <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontSize: '0.8rem', mt: 0.25 }}>
                        {analyzer.protocol?.toUpperCase() || 'ASTM'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: textSecondary, fontSize: '0.7rem' }}>MESSAGES</Typography>
                      <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontSize: '0.8rem', mt: 0.25 }}>
                        {analyzer.stats?.totalMessagesReceived || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: textSecondary, fontSize: '0.7rem' }}>RESULTS</Typography>
                      <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontSize: '0.8rem', mt: 0.25 }}>
                        {analyzer.stats?.totalResultsProcessed || 0}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Last activity */}
                  {analyzer.lastMessageAt && (
                    <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mt: 1.5, fontSize: '0.7rem' }}>
                      Last message: {new Date(analyzer.lastMessageAt).toLocaleString()}
                    </Typography>
                  )}

                  {analyzer.lastError && (
                    <Alert severity="error" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
                      {analyzer.lastError}
                    </Alert>
                  )}

                  {/* Test result */}
                  {testResult?.id === analyzer._id && (
                    <Alert
                      severity={testResult.connected ? 'success' : 'error'}
                      sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
                      onClose={() => setTestResult(null)}
                    >
                      {testResult.connected ? 'Connection successful' : `Connection failed: ${testResult.error}`}
                    </Alert>
                  )}
                </Box>

                {/* Card Actions */}
                <Box sx={{
                  px: 1.5, py: 1, display: 'flex', gap: 0.5,
                  borderTop: `1px solid ${borderColor}`, bgcolor: headerBg
                }}>
                  <Tooltip title="Run Simulation">
                    <IconButton size="small" onClick={() => handleRunSimulation(analyzer._id)}
                      disabled={simulatingId === analyzer._id}
                      sx={{ color: '#10B981' }}>
                      {simulatingId === analyzer._id ? <CircularProgress size={16} /> : <PlayArrow sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Test Connection">
                    <IconButton size="small" onClick={() => handleTestConnection(analyzer._id)}
                      disabled={testingId === analyzer._id}
                      sx={{ color: textSecondary }}>
                      {testingId === analyzer._id ? <CircularProgress size={16} /> : <Cable sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Message Log">
                    <IconButton size="small" onClick={() => openMessages(analyzer)} sx={{ color: textSecondary }}>
                      <Visibility sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(analyzer)} sx={{ color: textSecondary }}>
                      <Edit sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => setDeleteConfirmId(analyzer._id)} sx={{ color: '#EF4444' }}>
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ═══ CREATE / EDIT DIALOG ═══ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '640px', md: '680px' },
            maxWidth: '700px !important',
            maxHeight: '88vh',
            borderRadius: '14px',
            bgcolor: cardBg,
            backgroundImage: 'none',
            color: textPrimary,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${borderColor}`, color: textPrimary, fontSize: '1.05rem', fontWeight: 700 }}>
          {editingId ? 'Edit Analyzer' : 'Register Analyzer'}
          <IconButton onClick={() => setDialogOpen(false)} size="small" sx={{ color: textSecondary }}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5, overflowY: 'auto' }}>
          <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)} sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}>
            <Tab label="General" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Connection" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Protocol" sx={{ textTransform: 'none', fontWeight: 600 }} />
          </Tabs>

          {/* General Tab */}
          {selectedTab === 0 && (
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField label="Device Name" fullWidth size="small" required
                  placeholder="e.g., Mindray BC-5380"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Analyzer ID" fullWidth size="small" required
                  placeholder="e.g., MINDRAY-BC5380-01"
                  helperText="Must match TestCatalog analyzer mappings"
                  value={form.analyzerId} onChange={e => setForm(f => ({ ...f, analyzerId: e.target.value }))}
                  disabled={!!editingId}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Manufacturer" fullWidth size="small"
                  placeholder="e.g., Mindray"
                  value={form.manufacturer} onChange={e => setForm(f => ({ ...f, manufacturer: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Model" fullWidth size="small"
                  placeholder="e.g., BC-5380"
                  value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Serial Number" fullWidth size="small"
                  value={form.serialNumber} onChange={e => setForm(f => ({ ...f, serialNumber: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Department</InputLabel>
                  <Select value={form.department} label="Department"
                    onChange={e => setForm(f => ({ ...f, department: e.target.value }))}>
                    <MenuItem value="">None</MenuItem>
                    {departments.map(d => (
                      <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
                  label="Active"
                />
              </Grid>
            </Grid>
          )}

          {/* Connection Tab */}
          {selectedTab === 1 && (
            <Stack spacing={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Connection Type</InputLabel>
                <Select value={form.connectionType} label="Connection Type"
                  onChange={e => setForm(f => ({ ...f, connectionType: e.target.value }))}>
                  <MenuItem value="tcp">TCP/IP (Network)</MenuItem>
                  <MenuItem value="serial">Serial (RS-232)</MenuItem>
                </Select>
              </FormControl>

              {form.connectionType === 'tcp' && (
                <>
                  <TextField label="Host / IP Address" fullWidth size="small"
                    placeholder="e.g., 192.168.1.100 or leave empty for any"
                    value={form.tcpHost} onChange={e => setForm(f => ({ ...f, tcpHost: e.target.value }))}
                  />
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <TextField label="Port" fullWidth size="small" type="number"
                        value={form.tcpPort} onChange={e => setForm(f => ({ ...f, tcpPort: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Mode</InputLabel>
                        <Select value={form.tcpMode} label="Mode"
                          onChange={e => setForm(f => ({ ...f, tcpMode: e.target.value }))}>
                          <MenuItem value="server">Server (listen)</MenuItem>
                          <MenuItem value="client">Client (connect)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" sx={{ color: textSecondary }}>
                    Server mode: DycoLIS listens on this port for analyzer connections. Client mode: DycoLIS connects to the analyzer at the specified host:port.
                  </Typography>
                </>
              )}

              {form.connectionType === 'serial' && (
                <>
                  <TextField label="COM Port" fullWidth size="small"
                    placeholder="e.g., COM3 or /dev/ttyUSB0"
                    value={form.serialPort} onChange={e => setForm(f => ({ ...f, serialPort: e.target.value }))}
                  />
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Baud Rate</InputLabel>
                        <Select value={form.baudRate} label="Baud Rate"
                          onChange={e => setForm(f => ({ ...f, baudRate: e.target.value }))}>
                          {[2400, 4800, 9600, 19200, 38400, 57600, 115200].map(r => (
                            <MenuItem key={r} value={r}>{r}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Data Bits</InputLabel>
                        <Select value={form.dataBits} label="Data Bits"
                          onChange={e => setForm(f => ({ ...f, dataBits: e.target.value }))}>
                          {[7, 8].map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Parity</InputLabel>
                        <Select value={form.parity} label="Parity"
                          onChange={e => setForm(f => ({ ...f, parity: e.target.value }))}>
                          {['none', 'even', 'odd'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Stop Bits</InputLabel>
                        <Select value={form.stopBits} label="Stop Bits"
                          onChange={e => setForm(f => ({ ...f, stopBits: e.target.value }))}>
                          {[1, 2].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </>
              )}
            </Stack>
          )}

          {/* Protocol Tab */}
          {selectedTab === 2 && (
            <Stack spacing={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Protocol</InputLabel>
                <Select value={form.protocol} label="Protocol"
                  onChange={e => setForm(f => ({ ...f, protocol: e.target.value }))}>
                  <MenuItem value="astm">ASTM E1381 / E1394</MenuItem>
                  <MenuItem value="hl7">HL7 v2</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Sample ID Source</InputLabel>
                <Select value={form.sampleIdSource} label="Sample ID Source"
                  onChange={e => setForm(f => ({ ...f, sampleIdSource: e.target.value }))}>
                  <MenuItem value="order_sample_id">Order Record → Sample ID (default)</MenuItem>
                  <MenuItem value="order_instrument_id">Order Record → Instrument Specimen ID</MenuItem>
                  <MenuItem value="patient_id">Patient Record → Patient ID</MenuItem>
                </Select>
              </FormControl>
              <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                Sample ID Source tells DycoLIS where to find the barcode/specimen ID in the analyzer's ASTM messages. Most analyzers use the Order Record's Sample ID field.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: textSecondary }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name || !form.analyzerId}
            sx={{ textTransform: 'none', bgcolor: accentBlue, '&:hover': { bgcolor: '#2563EB' } }}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            {editingId ? 'Update' : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ DELETE CONFIRM DIALOG ═══ */}
      <Dialog
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '440px' },
            maxWidth: '460px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
            bgcolor: cardBg,
            backgroundImage: 'none',
            color: textPrimary,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${borderColor}`, color: textPrimary, fontSize: '1.05rem', fontWeight: 700 }}>
          Delete Analyzer?
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 2 }}>
          <Typography variant="body2" sx={{ color: textSecondary }}>
            This will permanently remove this analyzer device and all its configuration. Message logs will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setDeleteConfirmId(null)} sx={{ textTransform: 'none', color: textSecondary }}>Cancel</Button>
          <Button onClick={() => handleDelete(deleteConfirmId)} color="error" variant="contained" sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ MESSAGE LOG DIALOG ═══ */}
      <Dialog
        open={messagesDialog.open}
        onClose={() => setMessagesDialog({ open: false, analyzerId: null, analyzerName: '' })}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '860px', md: '920px' },
            maxWidth: '960px !important',
            maxHeight: '88vh',
            borderRadius: '14px',
            bgcolor: cardBg,
            backgroundImage: 'none',
            color: textPrimary,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${borderColor}`, color: textPrimary, fontSize: '1.05rem', fontWeight: 700 }}>
          Message Log — {messagesDialog.analyzerName}
          <IconButton onClick={() => setMessagesDialog({ open: false, analyzerId: null, analyzerName: '' })} size="small" sx={{ color: textSecondary }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5, overflowY: 'auto' }}>
          {messagesLoading ? (
            <LinearProgress />
          ) : messages.length === 0 ? (
            <Typography variant="body2" sx={{ color: textSecondary, textAlign: 'center', py: 4 }}>
              No messages received yet from this analyzer.
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: '50vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: textSecondary, fontWeight: 600, fontSize: '0.75rem' }}>Time</TableCell>
                    <TableCell sx={{ color: textSecondary, fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell sx={{ color: textSecondary, fontWeight: 600, fontSize: '0.75rem' }}>Samples</TableCell>
                    <TableCell sx={{ color: textSecondary, fontWeight: 600, fontSize: '0.75rem' }}>Results</TableCell>
                    <TableCell sx={{ color: textSecondary, fontWeight: 600, fontSize: '0.75rem' }}>Source</TableCell>
                    <TableCell sx={{ color: textSecondary, fontWeight: 600, fontSize: '0.75rem' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messages.map((msg) => (
                    <React.Fragment key={msg._id}>
                      <TableRow hover sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedRow(expandedRow === msg._id ? null : msg._id)}>
                        <TableCell sx={{ color: textPrimary, fontSize: '0.8rem' }}>
                          {new Date(msg.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={msg.status}
                            color={msg.status === 'processed' ? 'success' : msg.status === 'failed' ? 'error' : 'warning'}
                            variant="outlined" sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: textPrimary, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                          {msg.sampleIds?.join(', ') || '—'}
                        </TableCell>
                        <TableCell sx={{ color: textPrimary, fontSize: '0.8rem' }}>
                          {msg.resultCount?.matched || 0} / {msg.resultCount?.extracted || 0}
                        </TableCell>
                        <TableCell sx={{ color: textSecondary, fontSize: '0.8rem' }}>
                          {msg.sourceAddress}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small">
                            {expandedRow === msg._id ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                          <Collapse in={expandedRow === msg._id}>
                            <Box sx={{ py: 1.5, px: 1 }}>
                              {msg.errorMessage && (
                                <Alert severity="error" sx={{ mb: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                                  {msg.errorMessage}
                                </Alert>
                              )}
                              <Typography variant="caption" sx={{ color: textSecondary, display: 'block' }}>
                                Session: {msg.sessionId || '—'} • Protocol: {msg.protocol} •
                                Direction: {msg.direction} •
                                Extracted: {msg.resultCount?.extracted || 0} •
                                Matched: {msg.resultCount?.matched || 0} •
                                Unmatched: {msg.resultCount?.unmatched || 0}
                              </Typography>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {messagesPagination.total > 20 && (
            <TablePagination
              component="div"
              count={messagesPagination.total}
              page={messagesPagination.page}
              onPageChange={(_, p) => fetchMessagesPage(p)}
              rowsPerPage={20}
              rowsPerPageOptions={[20]}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ═══ SIMULATION RESULTS DIALOG ═══ */}
      <Dialog
        open={simDialogOpen}
        onClose={() => setSimDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '600px', md: '640px' },
            maxWidth: '660px !important',
            maxHeight: '88vh',
            borderRadius: '14px',
            bgcolor: cardBg,
            backgroundImage: 'none',
            color: textPrimary,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${borderColor}`, color: textPrimary, fontSize: '1.05rem', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BiotechOutlined sx={{ color: '#10B981' }} />
            Simulation Results
          </Box>
          <IconButton onClick={() => setSimDialogOpen(false)} size="small" sx={{ color: textSecondary }}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5, overflowY: 'auto' }}>
          {simResult && (
            <Box>
              <Alert
                severity={simResult.data?.processed > 0 ? 'success' : simResult.data?.errors?.length > 0 ? 'error' : 'info'}
                sx={{ mb: 2 }}
              >
                {simResult.message || 'Simulation completed'}
              </Alert>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: isDark ? '#1A2332' : '#F0FDF4', borderRadius: 2, border: '1px solid', borderColor: '#10B981' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#10B981' }}>
                      {simResult.data?.processed || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: textSecondary }}>Processed</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: isDark ? '#1A1A2E' : '#FFF7ED', borderRadius: 2, border: '1px solid', borderColor: '#F59E0B' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#F59E0B' }}>
                      {simResult.data?.skipped || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: textSecondary }}>Skipped</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: isDark ? '#2A1A1A' : '#FEF2F2', borderRadius: 2, border: '1px solid', borderColor: '#EF4444' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#EF4444' }}>
                      {simResult.data?.errors?.length || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: textSecondary }}>Errors</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {simResult.data?.details && simResult.data.details.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 600, mb: 1 }}>
                    Details
                  </Typography>
                  <Paper sx={{ p: 1.5, bgcolor: isDark ? '#141414' : '#F8FAFC', borderRadius: 1, maxHeight: 180, overflow: 'auto' }}>
                    {simResult.data.details.map((detail, idx) => (
                      <Typography key={idx} variant="body2" sx={{
                        color: detail.includes('ERROR') ? '#EF4444' : detail.includes('skipped') ? '#F59E0B' : textPrimary,
                        fontSize: '0.8rem', fontFamily: 'monospace', py: 0.3
                      }}>
                        {detail}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setSimDialogOpen(false)} sx={{ textTransform: 'none', color: textSecondary }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
